import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Skeleton } from '../../components/Skeleton'

interface Episode {
  id: number
  title: string
  published_at: string | null
  transcript: string | null
  transcript_source: string | null
  prediction_count: number | string
}

interface Influencer {
  id: number
  name: string
}

export default function Episodes() {
  const { id } = useParams<{ id: string }>()
  const [episodes, setEpisodes] = useState<Episode[]>([])
  const [influencer, setInfluencer] = useState<Influencer | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)

  useEffect(() => {
    if (!id) return
    fetch(`/api/intel/influencers/${id}/episodes`)
      .then(r => r.ok ? r.json() as Promise<Episode[]> : Promise.resolve([]))
      .then(setEpisodes)
      .catch(() => {})
    fetch('/api/intel/influencers')
      .then(r => r.ok ? r.json() as Promise<Influencer[]> : Promise.resolve([]))
      .then(list => setInfluencer(list.find(i => i.id === parseInt(id!)) ?? null))
      .catch(() => {})
  }, [id])

  return (
    <div>
      <Link to="/intel/influencers" className="text-sm text-gray-500 hover:text-gray-700">&larr; Back</Link>
      <h2 className="text-xl sm:text-2xl font-bold mt-2 mb-1 text-gray-900">
        {influencer?.name ? `${influencer.name} — Episodes` : <Skeleton className="h-8 w-64" />}
      </h2>
      {influencer && (
        <p className="text-xs text-gray-500 mb-4">
          <Link
            to={`/intel/episodes?influencer=${id}`}
            className="text-blue-500 hover:underline"
          >
            View in Episodes tab &rarr;
          </Link>
        </p>
      )}

      {episodes.length === 0 ? (
        <p className="text-gray-500">No episodes found. Run fetch-episodes first.</p>
      ) : (
        <div className="space-y-3">
          {episodes.map(ep => (
            <div key={ep.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-medium text-sm text-gray-900">{ep.title}</h3>
                  <div className="flex gap-3 mt-1 text-xs text-gray-500">
                    <span>{ep.published_at}</span>
                    <span>{ep.transcript ? `${ep.transcript_source}` : 'No transcript'}</span>
                    <span>{Number(ep.prediction_count)} predictions</span>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  {ep.transcript ? (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                      Transcribed
                    </span>
                  ) : (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                      Pending
                    </span>
                  )}
                </div>
              </div>

              {ep.transcript && (
                <button
                  onClick={() => setExpandedId(expandedId === ep.id ? null : ep.id)}
                  className="text-xs text-gray-500 hover:text-gray-700 mt-2"
                >
                  {expandedId === ep.id ? 'Hide transcript' : 'Show full transcript'}
                </button>
              )}

              {/* CHO-198: full transcript, no truncation, scrollable */}
              {expandedId === ep.id && ep.transcript && (
                <div className="mt-2 bg-gray-50 rounded p-3 max-h-[60vh] overflow-y-auto">
                  <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed font-mono">
                    {ep.transcript}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
